import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Printer, X } from "lucide-react";
import type { PrintItem, ActionItem } from "./FormControlPanel";

interface SelectionActionsProps {
    count: number;
    onClear: () => void;
    onDelete: () => void;
    deleting?: boolean;
    duplicating?: boolean;
    archiving?: boolean;
    onDuplicate?: () => void;
    onArchive?: () => void;
    // Custom action items declared by the action manifest (and ultimately the
    // DSL) — these render ABOVE the standard Duplicate/Archive/Delete block,
    // separated by a divider. Each item.onSelect performs the bulk dispatch.
    customItems?: ActionItem[];
    // Dynamic print items — built upstream from /api/reports/_list with mode
    // = "list" and the current selection as the target ids. Empty list → the
    // Print button hides entirely.
    printItems?: PrintItem[];
}

// Shown in place of the search box while rows are selected (list mode only).
// Live count + Print ▾ (dynamic from the report lister) + Actions ▾
// (custom verbs + standard Duplicate/Archive/Delete).
export function SelectionActions({
    count,
    onClear,
    onDelete,
    deleting,
    duplicating,
    archiving,
    onDuplicate,
    onArchive,
    customItems = [],
    printItems = [],
}: SelectionActionsProps) {
    return (
        <div className="selection-actions">
            <span className="selection-actions__count selection-actions__count--all">
                {count} selected
                <button className="selection-actions__select-all-close" onClick={onClear} aria-label="Clear selection">
                    <X size={13} />
                </button>
            </span>

            {printItems.length > 0 ? (
                <div className="selection-actions__menu">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <button type="button" className="btn">
                                <Printer size={14} />
                                Print
                                <ChevronDown size={13} />
                            </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content className="rdx-menu" align="end" sideOffset={4}>
                                {printItems.map((it) => (
                                    <DropdownMenu.Item
                                        key={it.key}
                                        className="rdx-menu__item"
                                        onSelect={it.onSelect}
                                    >
                                        <span className="rdx-menu__item-primary">{it.label}</span>
                                    </DropdownMenu.Item>
                                ))}
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                </div>
            ) : null}

            <div className="selection-actions__menu">
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                        <button type="button" className="btn">
                            Actions
                            <ChevronDown size={13} />
                        </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.Content className="rdx-menu" align="end" sideOffset={4}>
                            {/* Custom action verbs (top) — declared by the action
                                manifest. Each dispatches its own bulk handler. */}
                            {customItems.map((it) => (
                                <DropdownMenu.Item
                                    key={it.key}
                                    className="rdx-menu__item"
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        it.onSelect();
                                    }}
                                >
                                    <span className="rdx-menu__item-primary" style={it.danger ? { color: "var(--negative)" } : undefined}>
                                        {it.label}
                                    </span>
                                </DropdownMenu.Item>
                            ))}
                            {customItems.length > 0 ? <div className="menu-group__sep" role="separator" /> : null}

                            {/* Standard bulk actions block (Duplicate / Archive / Delete). */}
                            {onDuplicate ? (
                                <DropdownMenu.Item
                                    className="rdx-menu__item"
                                    disabled={duplicating}
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        onDuplicate();
                                    }}
                                >
                                    <span className="rdx-menu__item-primary">
                                        {duplicating ? "Duplicating…" : `Duplicate ${count > 1 ? count : ""}`.trim()}
                                    </span>
                                </DropdownMenu.Item>
                            ) : null}
                            {onArchive ? (
                                <DropdownMenu.Item
                                    className="rdx-menu__item"
                                    disabled={archiving}
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        onArchive();
                                    }}
                                >
                                    <span className="rdx-menu__item-primary">
                                        {archiving ? "Archiving…" : `Archive ${count > 1 ? count : ""}`.trim()}
                                    </span>
                                </DropdownMenu.Item>
                            ) : null}
                            <DropdownMenu.Item
                                className="rdx-menu__item"
                                disabled={deleting}
                                onSelect={(e) => {
                                    e.preventDefault();
                                    // onDelete shows our themed ConfirmDialog before
                                    // running the bulk delete; result of that dialog
                                    // gates the actual record removal.
                                    onDelete();
                                }}
                            >
                                <span className="rdx-menu__item-primary" style={{ color: "var(--negative)" }}>
                                    {deleting ? "Deleting…" : `Delete ${count}`}
                                </span>
                            </DropdownMenu.Item>
                        </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            </div>
        </div>
    );
}
